import CustomAutoComplete, {
  Option,
} from "@/modules/common/components/input/dropdowns/auto-complete";
import DatePickerInput from "@/modules/common/components/input/date-picker";
import StyledTextFieldWithTitle from "@/modules/common/components/input/styled-text-field-with-title";
import ReadonlyWrapper from "@/modules/common/components/wrappers/readonly-wrapper";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  useTheme,
} from "@mui/material";
import { NwButton } from "@nawy/ui";
import { Plus } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import useAddBrokerShowing from "../../hooks/broker-showing/use-add-broker-showing.hook";
import useBrokerOptions from "../../hooks/broker-showing/use-broker-options.hook";

const BROKER_SHOWING_EVENT: Option = {
  label: "Broker Showing",
  value: "broker_showing",
};

const EVENT_TYPE_OPTIONS: Option[] = [BROKER_SHOWING_EVENT];

interface BrokerShowingFormValues {
  broker: Option | null;
  showingDate: Date | string | null;
  tenantName: string;
}

const AddBrokerShowing = ({ trackUId }: { trackUId: string }) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const [eventType, setEventType] = useState<Option | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { isValid },
  } = useForm<BrokerShowingFormValues>({
    mode: "onChange",
    defaultValues: { broker: null, showingDate: null, tenantName: "" },
  });

  const {
    options: brokerOptions,
    isLoading: brokerOptionsLoading,
    handleSearchChange: handleBrokerSearch,
  } = useBrokerOptions();

  const { addBrokerShowing, isAddingBrokerShowing } = useAddBrokerShowing({
    trackUId,
    onSuccess: () => handleClose(),
  });

  const handleClose = () => {
    reset();
    setEventType(null);
    setOpen(false);
  };

  const onSubmit = handleSubmit((values) => {
    if (!values.broker || !values.showingDate) return;

    const showingDate =
      values.showingDate instanceof Date
        ? values.showingDate
        : new Date(values.showingDate);

    addBrokerShowing({
      brokerUid: values.broker.value,
      showingDate: showingDate.toISOString(),
      tenantName: values.tenantName.trim(),
    });
  });

  const isBrokerShowing = eventType?.value === BROKER_SHOWING_EVENT.value;

  return (
    <ReadonlyWrapper>
      <Box sx={{ px: theme.nawy.spacing.md, py: theme.nawy.spacing.xs }}>
        <NwButton
          variant="outlined"
          startIcon={<Plus size={16} />}
          onClick={() => setOpen(true)}
        >
          Add Event
        </NwButton>
      </Box>

      <Dialog open={open} onClose={handleClose} fullWidth maxWidth="tablet">
        <DialogTitle>Add Event</DialogTitle>
        <DialogContent>
          <Stack spacing={theme.nawy.spacing.md} sx={{ pt: theme.nawy.spacing.xs }}>
            <CustomAutoComplete
              label="Event type"
              placeholder="Select event type"
              required
              options={EVENT_TYPE_OPTIONS}
              value={eventType}
              onChange={(selected) =>
                setEventType((selected as Option | null) ?? null)
              }
            />

            {isBrokerShowing && (
              <>
                <Controller
                  control={control}
                  name="broker"
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <CustomAutoComplete
                      label="Broker name"
                      placeholder="Select broker"
                      required
                      error={!!fieldState.error}
                      options={brokerOptions}
                      loading={brokerOptionsLoading}
                      handleSearch={handleBrokerSearch}
                      value={field.value}
                      onChange={(selected) =>
                        field.onChange((selected as Option | null) ?? null)
                      }
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="showingDate"
                  rules={{ required: true }}
                  render={({ field, fieldState }) => (
                    <DatePickerInput
                      label="Showing date"
                      value={field.value}
                      onChange={field.onChange}
                      error={!!fieldState.error}
                    />
                  )}
                />

                <Controller
                  control={control}
                  name="tenantName"
                  rules={{ required: true, validate: (v) => !!v?.trim() }}
                  render={({ field, fieldState }) => (
                    <StyledTextFieldWithTitle
                      {...field}
                      title="Tenant name"
                      placeholder="Tenant name"
                      required
                      fullWidth
                      size="small"
                      error={!!fieldState.error}
                    />
                  )}
                />
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: theme.nawy.spacing.md, pb: theme.nawy.spacing.md }}>
          <NwButton variant="outlined" onClick={handleClose}>
            Cancel
          </NwButton>
          <NwButton
            onClick={onSubmit}
            disabled={!isBrokerShowing || !isValid || isAddingBrokerShowing}
          >
            {isAddingBrokerShowing ? <CircularProgress size={20} /> : "Submit"}
          </NwButton>
        </DialogActions>
      </Dialog>
    </ReadonlyWrapper>
  );
};

export default AddBrokerShowing;
